import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import { useLanguage } from '../../Hooks/useLanguage';
import { useRootStore } from '../../Store/useRootStore';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LanguageOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;

  &:hover {
    border-color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
    background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : Theme.Colors.lightestGrey)};
  }
`;

const Flag = styled.span<{ $selected?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    display: block;
    color: ${(props) => (props.$selected ? 'white' : Theme.Colors.warmGrey)};
  }
`;

const LanguageName = styled.span<{ $selected?: boolean }>`
  flex: 1;
  font-size: 15px;
  font-weight: ${(props) => (props.$selected ? '600' : '400')};
  color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.textColor)};
`;

const RadioIndicator = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 11px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background: ${(props) => (props.$selected ? Theme.Colors.primary : 'transparent')};
  }
`;

const BaseFlagIcon: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 21V4" />
    <path d="M4 5h15v10H4z" />
    {children}
  </svg>
);

const UkFlagIcon = () => (
  <BaseFlagIcon>
    <path d="M4 5l15 10" />
    <path d="M19 5L4 15" />
    <path d="M11.5 5v10" />
    <path d="M4 10h15" />
  </BaseFlagIcon>
);

const GermanyFlagIcon = () => (
  <BaseFlagIcon>
    <path d="M4 8.33h15" />
    <path d="M4 11.67h15" />
  </BaseFlagIcon>
);

const FranceFlagIcon = () => (
  <BaseFlagIcon>
    <path d="M9 5v10" />
    <path d="M14 5v10" />
  </BaseFlagIcon>
);

const ItalyFlagIcon = () => (
  <BaseFlagIcon>
    <path d="M9 5v10" />
    <path d="M14 5v10" />
  </BaseFlagIcon>
);

const LanguageFlagIcon: React.FC<{ code: string }> = ({ code }) => {
  switch (code) {
    case 'en':
      return <UkFlagIcon />;
    case 'de':
      return <GermanyFlagIcon />;
    case 'fr':
      return <FranceFlagIcon />;
    case 'it':
      return <ItalyFlagIcon />;
    default:
      return <BaseFlagIcon />;
  }
};

export type LanguageSelectorProps = {
  onSelect?: () => void;
};

export const LanguageSelector: React.FC<LanguageSelectorProps> = observer(({ onSelect }) => {
  const { language } = useLanguage();
  const { language: localization } = useRootStore();

  const handleSelect = useCallback(
    (code: string) => {
      localization.changeLanguage(code);
      onSelect?.();
    },
    [localization, onSelect],
  );

  return (
    <Container>
      {LANGUAGES.map((lang) => (
        <LanguageOption key={lang.code} $selected={language === lang.code} onClick={() => handleSelect(lang.code)}>
          <Flag $selected={language === lang.code}>
            <LanguageFlagIcon code={lang.code} />
          </Flag>
          <LanguageName $selected={language === lang.code}>{lang.name}</LanguageName>
          <RadioIndicator $selected={language === lang.code} />
        </LanguageOption>
      ))}
    </Container>
  );
});
